import React, { useEffect, useState } from 'react';
import {
  Prohibit, Info, ArrowClockwise, Check, X, ShieldCheck, Lock,
} from '@phosphor-icons/react';
import SearchableSelect from './SearchableSelect';
import SectionLock from './SectionLock';
import {
  getbranchnames, getChequeGeneralReceiptNumbers, getChequeGeneralReceiptNumberDetails,
  getChequeLiveDetails, reverseChequeDeposit, saveData,
} from '../api/connectionRegistryApi';
import { useLoginName, apiErrorMessage } from '../utils/auditUser';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';

function formatDisplayDate(d) {
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
}

const EMPTY_RECEIPT = {
  caoName: '', groupCode: '', ticketNo: '', subscriberName: '', chequeNo: '', cramount: '', dramount: '',
};

function DepositedChequeForm() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);

  const [branchList, setBranchList] = useState([]);
  const [receiptList, setReceiptList] = useState([]);

  const [branchCode, setBranchCode] = useState('');
  const [receiptNo, setReceiptNo] = useState('');
  const [receiptDate] = useState(() => formatDisplayDate(new Date()));
  const [fields, setFields] = useState(EMPTY_RECEIPT);
  const [reason, setReason] = useState('');

  // resolved from GetchequeliveDetails — needed by ReverseChequeDeposit
  const [contactId, setContactId] = useState(0);
  const [chitgroupId, setChitgroupId] = useState(0);
  const [transactionNo, setTransactionNo] = useState('');
  const [bankEntriesId, setBankEntriesId] = useState(0);
  const [receiptNumber, setReceiptNumber] = useState('');
  const [isCaoDone, setIsCaoDone] = useState(false);
  const [detailsLoaded, setDetailsLoaded] = useState(false);

  const [loadingDetails, setLoadingDetails] = useState(false);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAudit, setPendingAudit] = useState(null);
  const [pendingReason, setPendingReason] = useState('');
  const [pendingReceiptNo, setPendingReceiptNo] = useState('');
  const [pendingBranchName, setPendingBranchName] = useState('');

  const selectedBranch = branchList.find((b) => String(b.branchCode) === String(branchCode)) || null;

  useEffect(() => {
    getbranchnames()
      .then((res) => setBranchList(Array.isArray(res) ? res : []))
      .catch(() => showToast('error', 'Load Failed', 'Failed to load branches. Please try again.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const resetReceiptFields = () => {
    setFields(EMPTY_RECEIPT);
    setContactId(0);
    setChitgroupId(0);
    setTransactionNo('');
    setBankEntriesId(0);
    setReceiptNumber('');
    setIsCaoDone(false);
    setDetailsLoaded(false);
  };

  const handleBranchChange = async (value) => {
    setBranchCode(value || '');
    setReceiptNo('');
    setReceiptList([]);
    resetReceiptFields();
    if (!value) return;

    try {
      const res = await getChequeGeneralReceiptNumbers(value, value);
      setReceiptList(Array.isArray(res) ? res : []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch (err) {
      console.error('[getChequeGeneralReceiptNumbers] error =', err);
      setReceiptList([]);
      showToast('error', 'Load Failed', 'Failed to load receipt numbers. Please try again.');
    }
  };

  const handleReceiptChange = async (value) => {
    setReceiptNo(value || '');
    resetReceiptFields();
    if (!value) return;

    const receipt = receiptList.find((r) => String(r.general_receipt_number) === String(value));
    if (!receipt) return;

    setLoadingDetails(true);
    try {
      const detailsRes = await getChequeGeneralReceiptNumberDetails(branchCode, value);
      if (!Array.isArray(detailsRes) || detailsRes.length === 0) {
        showToast('warn', 'No Data Found', 'No details found for this receipt.');
        return;
      }

      const d0 = detailsRes[0];
      const depRefNo = d0.reference_number ?? d0.deposited_reference_no ?? '';
      const interbranchToUse = d0.interbranch_id ?? receipt.interbranch_id ?? 0;
      const resolvedReceiptNumber = String(d0.receipt_number ?? '');
      setReceiptNumber(resolvedReceiptNumber);

      const live = await getChequeLiveDetails(value, branchCode, interbranchToUse, depRefNo);
      if (!Array.isArray(live) || live.length === 0) {
        showToast('warn', 'No Data Found', 'No live cheque details found for this receipt.');
        return;
      }

      const d = live[0];
      const creditRow = live.find((x) => x.account_type === 'C') ?? null;
      const debitRow = live.find((x) => x.account_type === 'D') ?? null;

      setContactId(Number(d.contact_id ?? 0));
      setChitgroupId(Number(d.chitgroup_id ?? 0));
      setTransactionNo(String(d.transaction_no ?? ''));
      setBankEntriesId(Number(d.bank_entries_id ?? 0));

      // The Angular screen blocks the reversal once CAO has already receipted it.
      const rNum = d.receipt_number ?? '';
      const rDate = d.receipt_date ?? '';
      if (rNum !== '' && rDate !== '') {
        setIsCaoDone(true);
        showToast('warn', 'Receipt Locked', 'Receipt already done at CAO level. Please contact CAO.', true);
        return;
      }

      setFields({
        caoName: d.caoname ?? '',
        groupCode: d.groupcode ?? '',
        ticketNo: d.ticketno ?? '',
        subscriberName: d.contact_name ?? '',
        chequeNo: d.deposited_reference_no ?? depRefNo ?? '',
        cramount: creditRow?.ledger_amount ?? creditRow?.total_paid_amount ?? '',
        dramount: debitRow?.ledger_amount ?? debitRow?.total_paid_amount ?? '',
      });
      setDetailsLoaded(true);
    } catch (err) {
      console.error('[deposited cheque details] error =', err);
      showToast('error', 'Load Failed', apiErrorMessage(err, 'Failed to load cheque details for that receipt.'));
    } finally {
      setLoadingDetails(false);
    }
  };

  const branchInvalid = touched && !branchCode;
  const receiptInvalid = touched && !receiptNo;
  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);

  const isValid = !!branchCode && !!receiptNo && detailsLoaded
    && reason.trim().length >= 10 && reason.length <= 200;

  const handleSave = () => {
    setTouched(true);

    if (isCaoDone) {
      showToast('warn', 'Receipt Locked', 'Receipt Done in CAO level. Please contact CAO.');
      return;
    }

    if (!isValid) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    const trimmedReason = reason.trim();

    setPendingReason(trimmedReason);
    setPendingReceiptNo(receiptNo);
    setPendingBranchName(selectedBranch?.branchName ?? branchCode);

    setPendingAudit({
      schemaName: branchCode,
      loginName,
      loginTime: new Date().toISOString(),
      changeType: 'CHEQUE_LIVE_CANCEL',
      oldData: {
        general_receipt_number: receiptNo,
        receipt_date: receiptDate,
        branch_code: branchCode,
        caoname: fields.caoName,
        groupcode: fields.groupCode,
        ticketno: fields.ticketNo,
        contact_name: fields.subscriberName,
        cheque_number: fields.chequeNo,
        credit_amount: fields.cramount,
        debit_amount: fields.dramount,
        transaction_no: transactionNo,
        receiptnumber: receiptNumber,
      },
      newData: { status: 'CANCELLED', reason: trimmedReason },
      reason: trimmedReason,
    });

    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
    setPendingAudit(null);
  };

  const confirmSave = async () => {
    if (!pendingAudit) return;

    setSaving(true);
    try {
      const res = await reverseChequeDeposit(
        fields.caoName ?? '',
        branchCode,
        chitgroupId,
        Number(fields.ticketNo ?? 0),
        contactId,
        String(receiptNo ?? ''),
        String(fields.chequeNo ?? ''),
        receiptNumber,
        bankEntriesId,
        transactionNo,
      );

      // ReverseChequeDeposit swallows its own exceptions into the returned string
      // and still answers 200, so the body is the only signal of real success.
      const text = typeof res === 'string' ? res : (res?.message ?? JSON.stringify(res ?? ''));
      const ok = res?.success === true || (typeof text === 'string' && text.toLowerCase().includes('success'));
      if (!ok) {
        setSaving(false);
        setShowConfirmModal(false);
        console.error('ReverseChequeDeposit did not confirm success:', res);
        showToast('error', 'Cancellation Failed', text || 'Cancel receipt failed. Please try again.', true);
        return;
      }

      try {
        await saveData([pendingAudit]);
        setSaving(false);
        setShowConfirmModal(false);
        showToast('success', 'Success', 'Receipt cancelled successfully and audit saved.', true);
        handleClear();
      } catch (auditErr) {
        setSaving(false);
        setShowConfirmModal(false);
        console.error('Audit save failed:', auditErr);
        showToast('warn', 'Partial Success', 'Receipt cancelled, but audit save failed.', true);
        handleClear();
      }
    } catch (err) {
      setSaving(false);
      setShowConfirmModal(false);
      console.error('ReverseChequeDeposit error:', err);
      showToast('error', 'Cancellation Failed', apiErrorMessage(err, 'Cancel receipt failed. Please try again.'), true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setBranchCode('');
    setReceiptNo('');
    setReceiptList([]);
    resetReceiptFields();
    setReason('');
    setTouched(false);
    setPendingAudit(null);
  };

  const ro = { fontSize: '13px', background: '#F9FAFB' };

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="deposited-cheque-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="deposited-cheque-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Prohibit size={18} color="#002FA7" />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Change Request</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Deposited Cheque Live</div>
          </div>
        </div>

        {/* SECTION A: Select Receipt */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Receipt</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch and receipt number to load the cheque details</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Branch Name <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`branch-${formKey}`}
              options={branchList}
              getLabel={(o) => o.branchName}
              getValue={(o) => o.branchCode}
              value={branchCode}
              onChange={handleBranchChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              error={branchInvalid}
              testId="deposited-cheque-branch"
            />
            {branchInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Branch is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Receipt No. <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`receipt-${formKey}`}
              options={receiptList}
              getLabel={(o) => o.general_receipt_number}
              getValue={(o) => o.general_receipt_number}
              value={receiptNo}
              onChange={handleReceiptChange}
              placeholder="Select receipt"
              emptyMessage="Select a branch first"
              disabled={!branchCode}
              error={receiptInvalid}
              testId="deposited-cheque-receipt"
            />
            {receiptInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Receipt No. is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>Receipt Date</label>
            <input className="input-field" style={ro} value={receiptDate} readOnly data-testid="deposited-cheque-receipt-date" />
          </div>
        </div>

        {loadingDetails && <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '8px' }}>Loading cheque details...</div>}

        {isCaoDone && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', padding: '12px', border: '1px solid #FDE68A', background: '#FFFBEB', borderRadius: '8px', fontSize: '12px', color: '#92400E' }} data-testid="deposited-cheque-cao-lock">
            <Lock size={14} weight="fill" /> Receipt already done at CAO level — this receipt cannot be cancelled here. Please contact CAO.
          </div>
        )}

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!detailsLoaded} hint="Select a receipt in section A first">
        {/* SECTION B: Receipt Details */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Receipt Details</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Read-only details loaded from the selected receipt</div>
          </div>
        </div>

        <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', padding: '14px', background: '#F9FAFB' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>CAO Name</label>
              <input className="input-field" style={ro} value={fields.caoName || ''} readOnly placeholder="—" data-testid="deposited-cheque-cao-name" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Group Code</label>
              <input className="input-field" style={ro} value={fields.groupCode || ''} readOnly placeholder="—" data-testid="deposited-cheque-group-code" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Ticket No.</label>
              <input className="input-field" style={ro} value={fields.ticketNo || ''} readOnly placeholder="—" data-testid="deposited-cheque-ticket-no" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Subscriber Name</label>
              <input className="input-field" style={ro} value={fields.subscriberName || ''} readOnly placeholder="—" data-testid="deposited-cheque-subscriber" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Cheque No.</label>
              <input className="input-field" style={ro} value={fields.chequeNo || ''} readOnly placeholder="—" data-testid="deposited-cheque-cheque-no" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Transaction No.</label>
              <input className="input-field" style={ro} value={transactionNo || ''} readOnly placeholder="—" data-testid="deposited-cheque-transaction-no" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Credit Amount (₹)</label>
              <input className="input-field" style={ro} value={fields.cramount ?? ''} readOnly placeholder="—" data-testid="deposited-cheque-cr-amount" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Debit Amount (₹)</label>
              <input className="input-field" style={ro} value={fields.dramount ?? ''} readOnly placeholder="—" data-testid="deposited-cheque-dr-amount" />
            </div>
          </div>
        </div>
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!detailsLoaded || isCaoDone} hint="Load a cancellable receipt in section A first">
        {/* SECTION C: Reason */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason for Cancellation</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
          <span style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
            {reason.length} / 200
          </span>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={4}
          maxLength={200}
          placeholder="Enter reason (min 10 characters)..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="deposited-cheque-reason"
        />
        {reasonInvalid && (
          <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
            {reason.trim().length === 0 ? 'Reason is required' : 'Reason must be at least 10 characters'}
          </div>
        )}
        </SectionLock>

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="deposited-cheque-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button type="button" onClick={handleSave} className="btn-primary" disabled={saving || isCaoDone || !detailsLoaded} data-testid="deposited-cheque-save-button">
              <Check size={14} style={{ marginRight: '4px' }} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '460px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="deposited-cheque-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <ShieldCheck size={18} color="#002FA7" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Receipt Cancellation</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to cancel receipt <strong>{pendingReceiptNo}</strong> at <strong>{pendingBranchName}</strong>.
                This reverses the deposited cheque entry. Please review before confirming.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                <div>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Group / Ticket</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{fields.groupCode || '—'} / {fields.ticketNo || '—'}</p>
                </div>
                <div>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Cheque No.</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{fields.chequeNo || '—'}</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="deposited-cheque-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={confirmSave} disabled={saving} className="btn-primary" data-testid="deposited-cheque-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Cancel Receipt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DepositedChequeForm;
